import { Dispatch, SetStateAction, useState, ChangeEvent, useCallback } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { GqlImageBanner } from 'types/graphql';
import useErnie from 'shared/hooks/use-ernie';
import useImageUploader from 'shared/hooks/use-image-uploader';
import { ImageBannerStepsEnums } from './expandable-step';

type UseAddEditImageBannerReturn = {
  banner: GqlImageBanner | undefined;
  setBannerData: (data: Partial<GqlImageBanner>) => void;
  currentStep: number;
  setStepNumber: (step: number) => void;
  setEditStatus: (key: string) => boolean;
  isDirty: boolean;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  handleFile: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
  isUploadingImage: boolean;
  isUploadingMobileImage: boolean;
  handleSave: () => Promise<void>;
  isSaving: boolean;
};

type UseAddEditImageBannerProps = {
  bannerToEdit: GqlImageBanner | null;
  onSave: (banner: GqlImageBanner) => Promise<void>;
  onClose: () => void;
  isScheduleEnabled: boolean;
};

export function useAddEditImageBanner({
  bannerToEdit,
  onSave,
  isScheduleEnabled,
}: UseAddEditImageBannerProps): UseAddEditImageBannerReturn {
  const showErnie = useErnie();
  const uploadImage = useImageUploader();
  const [currentStep, setCurrentStep] = useState(1);

  const [banner, setBanner] = useState<GqlImageBanner | undefined>(() => {
    if (bannerToEdit) {
      return {
        ...bannerToEdit,
        alt: bannerToEdit.alt ?? '',
        link: bannerToEdit.link ?? '',
        openInNewTab: bannerToEdit.openInNewTab ?? true,
        image: bannerToEdit.image,
        mobileImage: bannerToEdit.mobileImage,
        platforms: bannerToEdit.platforms ?? [],
      };
    }

    return {
      _id: '',
      alt: '',
      link: '',
      openInNewTab: true,
      image: '',
      mobileImage: '',
      platforms: [],
      position: 0,
    };
  });

  const [isDirty, setIsDirty] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingMobileImage, setIsUploadingMobileImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  function setStepNumber(newStep: number): void {
    setCurrentStep(newStep);
  }

  function setEditStatus(key: string): boolean {
    if (key === ImageBannerStepsEnums.configuration && banner) {
      return !!banner.image || !!banner.mobileImage;
    }

    if (key === ImageBannerStepsEnums.schedule && banner) {
      return isScheduleEnabled ? !!banner.image || !!banner.mobileImage : true;
    }

    if (key === ImageBannerStepsEnums.confirm && banner) {
      return !!banner.image || !!banner.mobileImage;
    }

    return false;
  }

  const setBannerData = useCallback((data: Partial<GqlImageBanner>): void => {
    setBanner((prev) => ({ ...prev, ...data } as GqlImageBanner));
  }, []);

  const handleFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = e.target.files?.[0];
      const isMobile = e.target.id === 'mobile-image-banner-file-input';

      if (!file) {
        return;
      }

      if (isMobile) {
        setIsUploadingMobileImage(true);
      } else {
        setIsUploadingImage(true);
      }

      try {
        const url = await uploadImage(file);

        if (url) {
          if (isMobile) {
            setBannerData({ mobileImage: url });
          } else {
            setBannerData({ image: url });
          }
          setIsDirty(true);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        showErnie('Failed to upload image. Please try again.', 'danger');
      } finally {
        if (isMobile) {
          setIsUploadingMobileImage(false);
        } else {
          setIsUploadingImage(false);
        }
      }
    },
    [uploadImage, showErnie, setBannerData]
  );

  async function handleSave(): Promise<void> {
    if (!banner) {
      return;
    }

    if (!banner._id) {
      banner._id = uuidV4();
    }

    setIsSaving(true);

    try {
      await onSave(banner);
      setIsDirty(false);
    } catch (error) {
      console.error('Error saving banner:', error);
    } finally {
      setIsSaving(false);
    }
  }

  return {
    banner,
    setBannerData,
    currentStep,
    setStepNumber,
    setEditStatus,
    isDirty,
    setIsDirty,
    handleFile,
    isUploadingImage,
    isUploadingMobileImage,
    handleSave,
    isSaving,
  };
}
